import axios from 'axios';
import { EntityLabel, User } from '@esg/esg-global-types';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { apollo_client } from '../app/db_util';
import { GET_USER_DETAILS_QUERY, GET_USERS_QUERY } from '../graphql/queries/user_management/user';
import {
  RESTORE_USER_MUTATOR,
  UPDATE_USER_MUTATOR
} from '../graphql/mutations/user_management/user';
import { auth } from '../google/firebase';
import { CLOUD_FUNCTION_URL } from '../../env';
import { getAuthHeaderForUser, type AuthHeader } from '../auth/auth.util';

// Singular and plural label for model entity.
export const user_label: EntityLabel = {
  one: 'User',
  many: 'Users'
};

// Singular and plural label for user type modal entity.
export const user_type_label: EntityLabel = {
  one: 'User Type',
  many: 'User Types'
};

/**
 * Function to query all user documents
 * @returns {Array<User>}
 */
export const getUsers = async (get_deleted: boolean) => {
  try {
    const users: Array<User> = await apollo_client
      .query({
        variables: {
          get_deleted: get_deleted
        },
        query: GET_USERS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.users;
      });
    return users;
  } catch (err) {
    const error = `Error while getting Users from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : '',
      variables: {
        get_deleted: get_deleted
      }
    })}`;
    throw new Error(`Error: getUsers: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to get full user document from firestore
 * @param {string} id ID of User to retrieve details for
 * @returns {User}
 */
export const getUserDetails = async (id: string) => {
  try {
    const user_doc: User = await apollo_client
      .query({
        variables: {
          user_id: id
        },
        query: GET_USER_DETAILS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.user_details;
      });

    return user_doc;
  } catch (err) {
    const error = `Error while getting User details from Firestore: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : '',
      variables: {
        id: id
      }
    })}`;
    throw new Error(`Error: getUserDetails: ${JSON.stringify(error)}.`);
  }
};

/**
 * @param {string} user_id ID of user to update
 * @param {User} updated_user Data to update User document with
 */
export const updateUser = async (user_id: string, updated_user: User) => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_USER_MUTATOR,
        variables: {
          user_id: user_id,
          updated_user: updated_user,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateUser;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/user.ts failed on an unknown error while calling updateUser.',
      {
        user_id: user_id,
        updated_user: updated_user
      },
      tracking_id
    );
  }
};

/**
 * Function to undelete a user in firestore
 * @param {string} user_id ID of user to restore
 * @returns {void}
 */
export const restoreUser = async (user_id: string) => {
  try {
    await apollo_client
      .mutate({
        mutation: RESTORE_USER_MUTATOR,
        variables: {
          user_id: user_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.restoreUser;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    log(
      'error',
      new MetadataError(
        err instanceof Error
          ? err.message
          : 'Error: lib/user_management/users.ts failed on an unknown error while calling restoreUser.',
        {
          user_id: user_id
        },
        tracking_id
      )
    );
    throw new Error(`Error: updateUser`);
  }
};

/**
 * Re-enables a user's Firebase Auth account (sets `disabled: false`) via the
 * `reAddUser` function. Deleting a user disables their Auth account, so a
 * restore that only un-deletes the Firestore document leaves the user unable to
 * sign in — and a password reset cannot recover a disabled account. Callers
 * must pair this with the Firestore restore. Safe no-op for SSO users who have
 * no Firebase Auth record.
 * @param {string} userId ID of the user to re-enable.
 * @returns {Promise<void>}
 */
export const reAddUser = async (userId: string): Promise<void> => {
  const authHeader: AuthHeader = await getAuthHeaderForUser();
  await axios.post(
    `${CLOUD_FUNCTION_URL}/reAddUser`,
    { id: userId },
    { headers: { ...authHeader } }
  );
};
